import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { 
  Clock, 
  Server,
  RefreshCw,
  Zap,
  ArrowUp,
  FileText
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import axios from '../../config/axios';
import TopNavigation from "./TopNavigation";

const PerformanceDashboard = () => {

    const [duration, setDuration] = useState("5/minutes");
    const [loading, setLoading] = useState(false);
    const [performanceReportData, setPerformanceReportData] = useState({});

    // ------------------------------
    // Fetch API every time duration changes
    // ------------------------------
    useEffect(() => {
        setLoading(true);
        const timer = setTimeout(() => {
            axios.post(`performance/get-data/${duration}`)
            .then(response => {
                setPerformanceReportData(response.data);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching performance report:', error);
                setLoading(false);
            });
        }, 400);
        return () => clearTimeout(timer);
    }, [duration]);

    // ------------------------------
    // Extract avgPerformance & compute latencies
    // ------------------------------
    const chartData = useMemo(() => {
        const avg = performanceReportData?.avgPerformance || {};

        return Object.entries(avg).map(([key, value]) => ({
            name: key,
            count: value.count,
            time: value.time,
            avg: value.count ? parseFloat((value.time / value.count).toFixed(2)) : 0
        }));

    }, [performanceReportData]);

    // ------------------------------
    // Compute overall average latency for the top card
    // ------------------------------
    const overallAvgLatency = useMemo(() => {
        if (!chartData.length) return 0;

        const total = chartData.reduce((sum, item) => sum + item.avg, 0);
        return (total / chartData.length).toFixed(2);

    }, [chartData]);

    return (
        <div className='mx-auto'>
            <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 font-sans p-4 md:p-8 transition-colors duration-200">
                <TopNavigation />
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                            <Server className="text-blue-600 dark:text-blue-500" />
                            System Performance
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
                            Monitoring System health and throughput
                        </p>
                    </div>

                    {/* Duration Select */}
                    <div className="flex items-center gap-3 bg-white dark:bg-slate-800 p-1.5 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                        <div className="flex items-center px-3 text-slate-500 dark:text-slate-400 text-sm font-medium">
                            <Clock size={16} className="mr-2" />
                            Duration:
                        </div>

                        <select
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                            className="bg-slate-100 dark:bg-slate-700 border-none rounded-lg text-sm font-semibold py-2 pl-3 pr-8 focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors text-slate-900 dark:text-white"
                        >
                            <option value="5/minutes">5 Min</option>
                            <option value="10/minutes">10 Min</option>
                            <option value="15/minutes">15 Min</option>
                            <option value="30/minutes">30 Min</option>
                            <option value="1/hours">1 Hour</option>
                            <option value="24/hours">24 Hours</option>
                        </select>

                        <button 
                            onClick={() => setDuration('5/minutes')}
                            className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-slate-700 rounded-lg transition-colors"
                            title="Refresh Data"
                        >
                            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
                        </button>
                    </div>
                </div>

                {/* Top Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    
                    {/* Processing Rate */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-slate-500 text-sm font-medium">Processing Rate</p>
                                <h3 className="text-3xl font-bold mt-2 text-slate-900">
                                    {parseFloat(performanceReportData?.perSecond) * 100 || 0}
                                </h3>
                                <p className="text-xs text-slate-400 mt-1">files / second</p>
                            </div>
                            <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
                                <Zap size={24} />
                            </div>
                        </div>
                    </div>

                    {/* Total Files */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <p className="text-slate-500 text-sm font-medium">Total File Processed</p>
                                <h3 className="text-3xl font-bold mt-2 text-slate-900">
                                    {performanceReportData?.avgPerformance?.validate_files?.count || 0}
                                </h3>
                            </div>
                            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-lg">
                                <FileText size={24} />
                            </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 py-1 px-2 rounded w-fit">
                            <ArrowUp size={14} />
                            <span>Cumulative Count</span>
                        </div>
                    </div>

                    {/* Avg Job Latency */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <p className="text-slate-500 text-sm font-medium">Avg Job Latency</p>
                                <h3 className="text-3xl font-bold mt-2 text-slate-900">
                                    {overallAvgLatency} ms
                                </h3>
                            </div>
                            <div className="p-3 bg-orange-50 text-orange-600 rounded-lg">
                                <Clock size={24} />
                            </div>
                        </div>

                        <div className="w-full bg-slate-100 rounded-full h-2 mt-2">
                            <div
                                className="bg-orange-500 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${Math.min(overallAvgLatency / 200 * 100, 100)}%` }}
                            ></div>
                        </div>
                    </div>
                </div>

                {/* Chart + Table */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

                    {/* Chart */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="font-semibold text-lg text-slate-800">Average Latency per Queue (ms)</h2>
                            <Server size={18} className="text-slate-400" />
                        </div>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData} layout="vertical" margin={{ left: 40 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f1f5f9" />
                                    <XAxis type="number" hide />
                                    <YAxis type="category" dataKey="name" width={120} />
                                    <Tooltip />
                                    <Bar dataKey="avg" fill="#6366f1" radius={[0, 4, 4, 0]} barSize={20} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-100">
                                    <tr>
                                        <th className="px-6 py-3 font-semibold">Queue Name</th>
                                        <th className="px-6 py-3 font-semibold text-right">Count</th>
                                        <th className="px-6 py-3 font-semibold text-right">Total Time</th>
                                        <th className="px-6 py-3 font-semibold text-right">Average</th>
                                        <th className="px-6 py-3 font-semibold text-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">

                                    {chartData.map((row, index) => (
                                        <tr key={index} className="hover:bg-slate-50 transition-colors">
                                            <td className="px-6 py-4 font-medium text-slate-900">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-2 h-2 rounded-full ${row.avg > 5000 ? 'bg-amber-400' : 'bg-blue-400'}`}></div>
                                                    {row.name}
                                                </div>
                                            </td>

                                            <td className="px-6 py-4 text-right font-mono text-slate-600">
                                                {row.count}
                                            </td>

                                            <td className="px-6 py-4 text-right font-mono text-slate-600">
                                                {row.time?.toLocaleString()} ms
                                            </td>

                                            <td className="px-6 py-4 text-right font-mono font-bold text-slate-700">
                                                {row.avg} ms
                                            </td>

                                            <td className="px-6 py-4 text-center">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.avg > 10000
                                                        ? 'bg-amber-100 text-amber-700'
                                                        : 'bg-green-100 text-green-700'
                                                    }`}>
                                                    {row.avg > 10000 ? 'Heavy Load' : 'Healthy'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}

                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default PerformanceDashboard;
